import type * as React from 'react';

import { cn } from '../lib/utils';

function Card({
    className,
    size = 'default',
    elevation = 'flat',
    ...props
}: React.ComponentProps<'div'> & {
    size?: 'default' | 'sm' | 'lg';
    elevation?: 'raised' | 'flat';
}) {
    return (
        <div
            data-slot="card"
            data-size={size}
            data-elevation={elevation}
            className={cn(
                'bg-card text-card-foreground border-border gap-6 overflow-hidden rounded-sm border py-6 has-[>img:first-child]:pt-0 data-[size=sm]:gap-3 data-[size=sm]:py-3 data-[size=lg]:px-4 data-[size=lg]:pt-5 data-[size=lg]:pb-10 data-[size=lg]:gap-9 xs:data-[size=lg]:px-8 xs:data-[size=lg]:pt-11 xs:data-[size=lg]:pb-12 *:[img:first-child]:rounded-t-sm *:[img:last-child]:rounded-b-sm group/card flex flex-col',
                elevation === 'raised' && 'shadow-sm',
                className,
            )}
            {...props}
        />
    );
}

function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="card-header"
            className={cn(
                'gap-1.5 px-6 group-data-[size=sm]/card:px-3 group-data-[size=lg]/card:px-0 [.border-b]:pb-6 group-data-[size=sm]/card:[.border-b]:pb-3 group/card-header @container/card-header grid auto-rows-min items-center has-data-[slot=card-action]:grid-cols-[1fr_auto] has-data-[slot=card-description]:grid-rows-[auto_auto]',
                className,
            )}
            {...props}
        />
    );
}

function CardTitle({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="card-title"
            className={cn(
                'text-lg leading-none font-semibold group-data-[size=sm]/card:text-sm',
                className,
            )}
            {...props}
        />
    );
}

function CardDescription({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="card-description"
            className={cn('text-muted-foreground text-base', className)}
            {...props}
        />
    );
}

// The action spans the description's row only when there is one. Spanning unconditionally
// invents an empty second row, and the action's area then picks up the header gap on top of
// the title's row, sitting it half a gap lower. row-end-3 rather than row-span-2, because the
// span shorthand sets both edges and would overwrite row-start-1.
function CardAction({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="card-action"
            className={cn(
                'col-start-2 row-start-1 self-start justify-self-end group-has-data-[slot=card-description]/card-header:row-end-3',
                className,
            )}
            {...props}
        />
    );
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="card-content"
            className={cn(
                'px-6 group-data-[size=sm]/card:px-3 group-data-[size=lg]/card:px-0',
                className,
            )}
            {...props}
        />
    );
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="card-footer"
            className={cn(
                'px-6 group-data-[size=sm]/card:px-3 group-data-[size=lg]/card:px-0 flex items-center',
                className,
            )}
            {...props}
        />
    );
}

export { Card, CardAction, CardContent, CardDescription, CardFooter, CardHeader, CardTitle };
